import React from 'react';
import utils from 'web3-utils';
import createForm from '@blockcatio/tabby-library/lib/components/form/createForm';
import EthereumAddressInput from '@blockcatio/tabby-library/lib/components/form/elements/EthereumAddressInput';
import TextInput from '@blockcatio/tabby-library/lib/components/form/elements/TextInput';
import GasSliderInput from '@blockcatio/tabby-library/lib/components/form/elements/GasSliderInput';
import joi from '@blockcatio/tabby-library/lib/services/joi';
import { ZERO_ADDRESS } from '@blockcatio/tabby-library/lib/constants';
import isNumber from '@blockcatio/tabby-library/lib/utilities/isNumber';
import FormattedWei from '@blockcatio/tabby-library/lib/components/FormattedWei';
import calculateGasSliderMax from '@blockcatio/tabby-library/lib/utilities/calculateGasSliderMax';
import RadioGroupInput from '@blockcatio/tabby-library/lib/components/form/elements/RadioGroupInput';

import SelectInputAddNew from './formElements/SelectInputAddNew';
import { validateBalance } from '../utilities/helpers';
import CustomTokenForm from './modals/CustomTokenForm';
import AdvancedOptionsLayout from './AdvancedOptionsLayout';
import PaymentFormLayout from './PaymentFormLayout';
import { MAX_TOKENS_TO_SEND, ADDRESS_CHECKSUM_WARNING } from '../constants';
import isValidFloat from '../utilities/isValidFloat';
import getPaymentDecimals from '../utilities/getPaymentDecimals';

const BigNumber = require('bignumber.js');

const PaymentForm = ({
  currentAddress,
  recommendedGasPrice,
  tokenList,
  tokenOptions,
  onSelectFormSubmit,
  onSelectModalClose,
  fetchTokenBalance,
  getAmountToSend,
  currentGasPrice,
  validateTokenAddress,
  payWithEthFee,
  payWithEthFeeFiat,
  payWithCatFee,
  payWithCatFeeFiat,
  calculateGasForward,
  ...props
}) =>
  createForm({
    ...props,
    fields: [
      {
        id: 'tokenAddress',
        label: 'Currency',
        inputComponent: SelectInputAddNew,
        required: true,
        inputOptions: {
          options: tokenOptions,
          formComponent: CustomTokenForm,
          onFormSubmit: onSelectFormSubmit,
          onModalClose: onSelectModalClose,
          formProps: {
            validateTokenAddress,
          },
        },
        validation: joi
          .ethereumAddress()
          .insensitive()
          .required(),
        defaultValue: ZERO_ADDRESS,
      },
      {
        id: 'amount',
        label: 'Amount to Send',
        inputComponent: TextInput,
        inputOptions: {
          placeholder: '0.001',
        },
        required: true,
        validation: joi
          .largeFloat()
          // Disallow sending 0.
          .greater('0')
          .required(),
        checks: [
          // Limit amount - depends on tokenAddress's value.
          ({ amount, tokenAddress }) => {
            if (isNumber(amount)) {
              // Cap ETH at 100, and tokens at a somewhat high amount.
              const maxAmount = tokenAddress === ZERO_ADDRESS ? '100' : MAX_TOKENS_TO_SEND;
              const units = tokenAddress === ZERO_ADDRESS ? 'ETH' : 'tokens';
              if (BigNumber(amount).gt(maxAmount)) {
                throw new Error(`We currently do not support transactions larger than ${maxAmount} ${units}`);
              }
            }
          },
          // Validate precision - need derived field - decimals.
          ({ amount, decimals }) => {
            if (!isValidFloat(amount, decimals)) {
              throw new Error(`Must have no more than ${decimals} decimal places`);
            }
          },
          // Balance check - needs derived decimals, plus external balance.
          async (input) => {
            if (isValidFloat(input.amount, input.decimals)) {
              const balance = await fetchTokenBalance(input.tokenAddress);
              // Convert amount to its true Integer form.
              const amount = getAmountToSend(input);
              if (!validateBalance(amount, balance)) {
                throw new Error('Insufficient funds');
              }
            }
          },
        ],
      },
      {
        id: 'destination',
        label: 'Address',
        inputComponent: EthereumAddressInput,
        fieldOptions: {
          className: 'input-with-icon',
        },
        required: true,
        validation: joi
          .ethereumAddress()
          .insensitive()
          .not(currentAddress)
          .required()
          .error(errors =>
            errors.map(error => ({
              ...error,
              message: error.type === 'any.invalid' ? 'You cannot send to yourself' : undefined,
            }))),
        warnings: [
          (input) => {
            if (
              utils.isAddress(input.destination) &&
              !utils.checkAddressChecksum(input.destination)
            ) {
              return ADDRESS_CHECKSUM_WARNING;
            }
            return '';
          },
        ],
      },
      {
        id: 'gasPrice',
        label: 'Gas Price',
        inputComponent: GasSliderInput,
        inputOptions: {
          recommendedGasPrice,
          format: value => `${value} Gwei`,
        },
        fieldComponent: AdvancedOptionsLayout,
        required: true,
        validation: joi
          .number()
          .precision(1)
          .min(1)
          .max(calculateGasSliderMax(recommendedGasPrice)),
        // Always use the recommended price for default if we have it.
        defaultValue: recommendedGasPrice || 3,
      },
      // TODO: add a check to show/hide this input.
      {
        id: 'paymentType',
        inputComponent: RadioGroupInput,
        required: true,
        inputOptions: {
          options: [
            {
              value: 'ETH',
              label: (
                <React.Fragment>
                  <h5>Pay with ETH</h5>
                  <FormattedWei wei={payWithEthFee} />
                  <em>{payWithEthFeeFiat}</em>
                </React.Fragment>
              ),
            },
            {
              value: 'CAT',
              label: (
                <React.Fragment>
                  <h5>Pay with CAT</h5>
                  <FormattedWei wei={payWithCatFee} suffix="CAT" />
                  <em>{payWithCatFeeFiat}</em>
                </React.Fragment>
              ),
            },
          ],
        },
        validation: joi.string(),
        defaultValue: 'ETH',
      },
    ],
    formComponent: PaymentFormLayout,
    formClassName: 'payment-form',
    submitText: 'Continue >',
    derivedFields: {
      decimals: ({ tokenAddress }) => getPaymentDecimals(tokenAddress, tokenList),
      gasForward: input => calculateGasForward(input),
    },
  });

PaymentForm.displayName = 'PaymentForm';

export default PaymentForm;



// WEBPACK FOOTER //
// ./src/components/PaymentForm.js